<template>
  <div class="lookup-page">
    <v-card class="lookup-card">
      <div class="lookup-header">
        <div class="lookup-header__copy">
          <div class="lookup-title">Universities</div>
          <div class="lookup-subtitle">Manage universities and institutional details.</div>
        </div>
        <v-text-field
            v-model="search"
            class="lookup-search"
            append-icon="mdi-magnify"
            hide-details
            label="Search"
            single-line
        ></v-text-field>
        <v-btn
            v-if="canCreateUniversities"
            class="lookup-add-button"
            depressed
            @click="addUniversity()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add University</span>
        </v-btn>
      </div>
      <v-data-table
          :headers="headers"
          :items="universities.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="universities.count"
          class="lookup-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.name`]="{ item }">
          <label for="item">{{ item.name ? item.name : "-" }}</label>
        </template>
        <template v-slot:[`item.code`]="{ item }">
          <label for="item">{{ item.code ? item.code : "-" }}</label>
        </template>
        <template v-if="canEditUniversities" v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                  class="mr-2 lookup-action-icon"
                  @click="editUniversity(item)"
                  v-on="on"
              >mdi-circle-edit-outline
              </v-icon
              >
            </template>
            <span>{{ title }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.isActive`]="{ item }">
          <v-chip :color="getColor(item.isActive)" dark>
            {{ item.isActive == true ? "Active" : "Not Active" }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="50%">
        <v-card class="lookup-dialog-card">
          <v-card-title
              class="lookup-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="lookup-dialog__close" color="white" text @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <AddUniversity
              v-if="dialog"
              :university="university"
              @close="closeDialog()"
              @registerOrUpdateUniversity="registerOrUpdateUniversity"
          ></AddUniversity>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import {getUser} from "@/utils/helper";
import AddUniversity from './add-university.vue';
import {LookupService} from "../../../common/api.service";
import {UniversitiesService} from "./universities.service";

export default {
  components: {
    AddUniversity
  },
  data() {
    return {
      search: "",
      searchDebounce: null,
      loading: false,
      dialog: false,
      title: "",
      universities: [],
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      university: null,
      headers: [
        {text: "University Name", value: "name"},
        {text: "Ownership", value: "ownership.name"},
        {text: "Generation", value: "generation"},
        {text: "Institution Type", value: "institutionType"},
        {text: "Region", value: "region.name"},
        {text: "Town", value: "town"},
        {text: "University Code", value: "code"},
        {text: "University Status", value: "isActive"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
    }
  },
  async beforeMount() {
    this.fetchUniversities();
  },
  watch: {
    options: {
      handler() {
        this.fetchUniversities();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchUniversities();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchUniversities();
      }, 400);
    },
  },
  computed: {
    canCreateUniversities() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Universities" && permission.canCreate
      );
    },
    canEditUniversities() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Universities" && permission.canUpdate
      );
    },
  },
  methods: {
    async fetchUniversities() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;
      const res = await LookupService.getUniversitiesWP(
          page,
          itemsPerPage,
          this.search
      );
      if (res.status === 200) {
        this.loading = false;
        this.universities = res.data;
      } else {
        this.loading = false;
        this.$toast.error(
            "There was a problem fetching Universities, Please try Again!"
        );
      }
    },
    addUniversity() {
      this.university = null;
      this.dialog = true;
      this.title = "Add University";

    },
    editUniversity(item) {
      this.university = item;
      this.dialog = true;
      this.title = "Edit University";
    },
    closeDialog() {
      this.dialog = false;
      this.fetchUniversities();
    },
    getColor(isActive) {
      if (isActive == false) return "red";
      else return "green";
    },
    async registerOrUpdateUniversity(university) {
      this.loading = true;
      let res = null;

      if (university.editMode) {
        res = await UniversitiesService.update(
            university.id,
            university
        );

        if (res.status === 201) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fetchUniversities();
              this.loading = false;
              break;
            case 500:
              this.$toast.error(res.data.message);
              this.loading = false;
              break;
          }
        } else {
          this.$toast.error(
              "Unable to update university, please try again"
          );
          this.loading = false;
        }
      } else {
        res = await UniversitiesService.create(university);

        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fetchUniversities();
              this.loading = false;

              break;
            case 500:
              this.$toast.error(res.data.message);
              this.loading = false;

              break;
          }
        } else {
          this.$toast.error(
              "Unable to create university, please try again"
          );
          this.loading = false;
        }
      }
      this.loading = false;

    }
  },
};
</script>

<style scoped>
.lookup-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.lookup-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.lookup-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.lookup-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lookup-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.lookup-subtitle {
  font-size: 14px;
  color: #475569;
}

.lookup-search {
  width: 100%;
  max-width: 360px;
  justify-self: center;
  align-self: center;
}

.lookup-table {
  border-radius: 0 0 12px 12px;
}

.lookup-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.lookup-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.lookup-dialog__close {
  color: #ffffff !important;
}

.lookup-dialog__close .v-icon {
  color: #ffffff !important;
}

.lookup-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.lookup-action-icon {
  color: #1976d2 !important;
}

@media (max-width: 700px) {
  .lookup-page {
    padding: 12px;
  }

  .lookup-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .lookup-search {
    max-width: 100%;
  }

  .lookup-add-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
